<template>
  <div
    v-loading="loading"
    element-loading-background="rgba(6, 12, 26, 0.65)"
    class="tech-dash"
  >
    <div class="dash-bg" aria-hidden="true">
      <span class="orb orb-1" />
      <span class="orb orb-2" />
      <span class="orb orb-3" />
    </div>

    <div class="dash-head">
      <div class="head-left">
        <div class="dash-title">运营数据可视化中心</div>
        <div class="dash-sub">宸镜AI · 全平台经营看板</div>
      </div>
      <div class="head-right">
        <div class="live-chip">
          <span class="live-dot" />在线 <b>{{ formatCount(stats.onlineUserCount) }}</b> 人
        </div>
        <div class="clock">{{ clockText }}</div>
        <el-button
          class="refresh-btn"
          size="mini"
          icon="el-icon-refresh"
          @click="loadStats"
        >刷新</el-button>
      </div>
    </div>

    <!-- 核心指标 -->
    <el-row :gutter="16">
      <el-col v-for="item in metricCards" :key="item.key" :xs="24" :sm="12" :lg="6">
        <div class="kpi-card" :style="{ '--accent': item.color }">
          <div class="kpi-head">
            <span class="kpi-label">{{ item.label }}</span>
            <span class="kpi-icon"><i :class="item.icon" /></span>
          </div>
          <div class="kpi-value">{{ item.displayValue }}</div>
          <div class="kpi-foot">
            <span class="growth" :class="growthClass(item.growth)">
              <i :class="growthIcon(item.growth)" />{{ formatGrowth(item.growth) }}
            </span>
            <span class="kpi-tip">较上月</span>
          </div>
          <svg v-if="item.spark" class="kpi-spark" viewBox="0 0 100 34" preserveAspectRatio="none">
            <defs>
              <linearGradient :id="'sparkFill-' + item.key" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" :stop-color="item.color" stop-opacity="0.45" />
                <stop offset="100%" :stop-color="item.color" stop-opacity="0" />
              </linearGradient>
            </defs>
            <path :d="item.spark.area" :fill="'url(#sparkFill-' + item.key + ')'" />
            <path :d="item.spark.line" fill="none" :stroke="item.color" stroke-width="1.6" />
          </svg>
        </div>
      </el-col>
    </el-row>

    <!-- 营收 / 成本 / 毛利 -->
    <el-row :gutter="16" class="panel-row">
      <el-col :xs="24" :lg="16">
        <div class="panel">
          <div class="panel-head">
            <span class="head-bar" />近 7 日 营收 · 成本 · 毛利
            <em v-if="!hasOpsTrend">暂无积分消耗流水</em>
          </div>
          <div ref="profitTrend" class="chart chart-lg" />
        </div>
      </el-col>
      <el-col :xs="24" :lg="8">
        <div class="panel">
          <div class="panel-head"><span class="head-bar" />今日毛利率</div>
          <div ref="profitGauge" class="chart chart-gauge" />
          <div class="mini-stats">
            <div
              v-for="item in profitMini"
              :key="item.key"
              class="mini-item"
              @click="goCard(item)"
            >
              <span class="mini-dot" :style="{ background: item.color }" />
              <span class="mini-label">{{ item.label }}</span>
              <span class="mini-value" :style="{ color: item.color }">{{ item.displayValue }}</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 增长 / 消耗 / 核算 -->
    <el-row :gutter="16" class="panel-row">
      <el-col :xs="24" :lg="8">
        <div class="panel">
          <div class="panel-head"><span class="head-bar" />近 7 日新增用户 · 需求</div>
          <div ref="growthTrend" class="chart chart-md" />
        </div>
      </el-col>
      <el-col :xs="24" :lg="8">
        <div class="panel">
          <div class="panel-head"><span class="head-bar" />近 7 日积分消耗 · 活跃</div>
          <div ref="creditTrend" class="chart chart-md" />
        </div>
      </el-col>
      <el-col :xs="24" :lg="8">
        <div class="panel">
          <div class="panel-head"><span class="head-bar" />成本核算质量</div>
          <div ref="accountPie" class="chart chart-md" />
        </div>
      </el-col>
    </el-row>

    <!-- 待处理 / 测试消耗 -->
    <el-row :gutter="16" class="panel-row">
      <el-col :xs="24" :lg="16">
        <div class="panel">
          <div class="panel-head">
            <span class="head-bar" />待处理事项
            <em>点击条目直达处理页</em>
          </div>
          <div ref="pendingBar" class="chart chart-md" />
        </div>
      </el-col>
      <el-col :xs="24" :lg="8">
        <div class="panel">
          <div class="panel-head"><span class="head-bar" />测试消耗监控</div>
          <div ref="testRing" class="chart chart-ring" />
          <div class="mini-stats">
            <div
              v-for="item in testMini"
              :key="item.key"
              class="mini-item"
              @click="goCard(item)"
            >
              <span class="mini-dot" :style="{ background: item.color }" />
              <span class="mini-label">{{ item.label }}</span>
              <span class="mini-value" :style="{ color: item.color }">{{ item.displayValue }}</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 快捷入口 -->
    <div class="quick-row">
      <div
        v-for="item in quickEntries"
        :key="item.key"
        class="quick-chip"
        @click="goCard(item)"
      >
        <i :class="item.icon" />
        <span class="quick-label">{{ item.label }}</span>
        <span class="quick-value">{{ item.displayValue }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, GaugeChart, LineChart, PieChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { getDashboardStats } from '@/api/admin'
import {
  formatCount,
  formatGrowth,
  formatPercent,
  formatRevenue,
  formatYuan,
  growthClass
} from '@/utils/dashboardFormat'

echarts.use([
  BarChart,
  GaugeChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer
])

const AXIS_LABEL = { color: '#8ea3c0', fontSize: 11 }
const AXIS_LINE = { lineStyle: { color: 'rgba(142, 163, 192, 0.25)' } }
const SPLIT_LINE = { lineStyle: { color: 'rgba(142, 163, 192, 0.12)', type: 'dashed' } }
const TOOLTIP = {
  backgroundColor: 'rgba(8, 16, 34, 0.94)',
  borderColor: 'rgba(56, 189, 248, 0.35)',
  borderWidth: 1,
  textStyle: { color: '#dbeafe', fontSize: 12 },
  extraCssText: 'backdrop-filter: blur(6px); border-radius: 8px;'
}
const LEGEND_TEXT = { color: '#8ea3c0', fontSize: 11 }

const METRIC_DEFS = [
  {
    key: 'userCount',
    label: '总用户数',
    icon: 'el-icon-user-solid',
    color: '#38bdf8',
    growthKey: 'userGrowthRate',
    sparkKey: 'newUsers',
    formatter: formatCount
  },
  {
    key: 'demandCount',
    label: '总需求数',
    icon: 'el-icon-document',
    color: '#f59e0b',
    growthKey: 'demandGrowthRate',
    sparkKey: 'newDemands',
    formatter: formatCount
  },
  {
    key: 'workCount',
    label: '作品总数',
    icon: 'el-icon-video-camera-solid',
    color: '#a855f7',
    growthKey: 'workGrowthRate',
    formatter: formatCount
  },
  {
    key: 'revenueTotal',
    label: '平台营收',
    icon: 'el-icon-coin',
    color: '#10b981',
    growthKey: 'revenueGrowthRate',
    sparkKey: 'orderRevenueYuan',
    formatter: formatRevenue
  }
]

const PROFIT_MINI = [
  { key: 'todayRevenueYuan', label: '今日实收', color: '#22d3ee', formatter: formatYuan, path: '/admin/ops/profit' },
  { key: 'todayVolcCostYuan', label: '火山成本', color: '#f59e0b', formatter: formatYuan, path: '/admin/ops/profit' },
  { key: 'todayProfitYuan', label: '今日毛利', color: '#34d399', formatter: formatYuan, path: '/admin/ops/profit' }
]

const TEST_MINI = [
  { key: 'todayTestCostYuan', label: '测试成本', color: '#fb923c', formatter: formatYuan, path: '/admin/ops/test' },
  { key: 'todayTestNominalCredits', label: '名义积分', color: '#facc15', formatter: formatCount, path: '/admin/ops/test' },
  { key: 'todayTestConsumeCount', label: '消费笔数', color: '#f472b6', formatter: formatCount, path: '/admin/ops/test' }
]

const QUICK_DEFS = [
  { key: 'todayConsumeCredits', label: '今日积分消耗', icon: 'el-icon-s-data', formatter: formatCount, path: '/admin/ops/credits' },
  { key: 'onlineUserCount', label: '当前在线', icon: 'el-icon-s-custom', formatter: formatCount, path: '/admin/ops/online' },
  { key: 'todayActiveUsers', label: '今日活跃', icon: 'el-icon-s-opportunity', formatter: formatCount, path: '/admin/ops/active' },
  { key: 'todayProfitRate', label: '今日毛利率', icon: 'el-icon-data-analysis', formatter: formatPercent, path: '/admin/ops/profit', query: { status: 'accounted' } },
  { key: 'pointsOrderCount', label: '积分订单', icon: 'el-icon-s-order', formatter: formatCount, path: '/admin/points' }
]

const PENDING_DEFS = [
  { key: 'demandPendingCount', title: '待审核需求', path: '/admin/demands/list', color: '#38bdf8' },
  { key: 'workPendingCount', title: '待审核作品', path: '/admin/creations/works', color: '#a855f7' },
  { key: 'copyrightPendingCount', title: '版权申请', path: '/admin/creations/copyright', color: '#34d399' },
  { key: 'feedbackCount', title: '问题反馈', path: '/admin/feedback', color: '#f472b6' }
]

function gradient(from, to) {
  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: from },
      { offset: 1, color: to }
    ]
  }
}

function buildSpark(values) {
  const list = (values || []).map(v => Number(v || 0))
  if (list.length < 2) return null
  const max = Math.max.apply(null, list)
  const min = Math.min.apply(null, list)
  const span = max - min || 1
  const step = 100 / (list.length - 1)
  const points = list.map((v, i) => {
    const x = +(i * step).toFixed(2)
    const y = +(30 - ((v - min) / span) * 26).toFixed(2)
    return [x, y]
  })
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ')
  return { line, area: `${line} L100,34 L0,34 Z` }
}

export default {
  name: 'AdminDashboard',
  data() {
    return {
      loading: false,
      stats: {},
      clockText: ''
    }
  },
  computed: {
    trend() {
      return this.stats.trend || []
    },
    hasOpsTrend() {
      return this.trend.some(item => Number(item.revenueYuan || 0) > 0 ||
        Number(item.costYuan || 0) > 0 ||
        Number(item.consumeCredits || 0) > 0)
    },
    metricCards() {
      return METRIC_DEFS.map(item => ({
        ...item,
        displayValue: item.formatter(this.stats[item.key]),
        growth: this.stats[item.growthKey],
        spark: item.sparkKey ? buildSpark(this.trend.map(row => row[item.sparkKey])) : null
      }))
    },
    profitMini() {
      return PROFIT_MINI.map(item => ({
        ...item,
        displayValue: item.formatter(this.stats[item.key])
      }))
    },
    testMini() {
      return TEST_MINI.map(item => ({
        ...item,
        displayValue: item.formatter(this.stats[item.key])
      }))
    },
    quickEntries() {
      return QUICK_DEFS.map(item => ({
        ...item,
        displayValue: item.formatter(this.stats[item.key])
      }))
    }
  },
  created() {
    this.charts = {}
    this.loadStats()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.tickClock()
    this.clockTimer = setInterval(this.tickClock, 1000)
    this.pollTimer = setInterval(this.loadStats, 60000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    clearInterval(this.clockTimer)
    clearInterval(this.pollTimer)
    clearTimeout(this.resizeTimer)
    this.disposeCharts()
  },
  methods: {
    formatCount,
    formatGrowth,
    growthClass,
    growthIcon(value) {
      const num = Number(value || 0)
      if (num > 0) return 'el-icon-top'
      if (num < 0) return 'el-icon-bottom'
      return 'el-icon-minus'
    },
    tickClock() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.clockText = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },
    async loadStats() {
      if (this.loading) return
      this.loading = true
      try {
        this.stats = await getDashboardStats() || {}
        this.$nextTick(() => this.renderAll())
      } catch (e) {
        // 静默失败，保留上一次数据
      } finally {
        this.loading = false
      }
    },
    getChart(name) {
      const el = this.$refs[name]
      if (!el) return null
      if (!this.charts[name]) {
        this.charts[name] = echarts.init(el, null, { renderer: 'canvas' })
      }
      return this.charts[name]
    },
    renderAll() {
      this.renderProfitTrend()
      this.renderProfitGauge()
      this.renderGrowthTrend()
      this.renderCreditTrend()
      this.renderAccountPie()
      this.renderPendingBar()
      this.renderTestRing()
    },
    renderProfitTrend() {
      const chart = this.getChart('profitTrend')
      if (!chart) return
      const trend = this.trend
      chart.setOption({
        tooltip: {
          ...TOOLTIP,
          trigger: 'axis',
          axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(56, 189, 248, 0.08)' } },
          valueFormatter: value => `¥${Number(value || 0).toFixed(2)}`
        },
        legend: {
          data: ['充值订单', '用户实收', '火山成本', '毛利'],
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: LEGEND_TEXT
        },
        grid: { left: 12, right: 12, top: 24, bottom: 40, containLabel: true },
        xAxis: {
          type: 'category',
          data: trend.map(item => item.label),
          axisLine: AXIS_LINE,
          axisTick: { show: false },
          axisLabel: AXIS_LABEL
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: SPLIT_LINE,
          axisLabel: { ...AXIS_LABEL, formatter: value => `¥${value}` }
        },
        series: [
          {
            name: '充值订单',
            type: 'bar',
            barMaxWidth: 16,
            itemStyle: { color: gradient('#818cf8', 'rgba(129, 140, 248, 0.15)'), borderRadius: [4, 4, 0, 0] },
            data: trend.map(item => round2(item.orderRevenueYuan))
          },
          {
            name: '用户实收',
            type: 'bar',
            barMaxWidth: 16,
            itemStyle: { color: gradient('#22d3ee', 'rgba(34, 211, 238, 0.15)'), borderRadius: [4, 4, 0, 0] },
            data: trend.map(item => round2(item.revenueYuan))
          },
          {
            name: '火山成本',
            type: 'bar',
            barMaxWidth: 16,
            itemStyle: { color: gradient('#f59e0b', 'rgba(245, 158, 11, 0.15)'), borderRadius: [4, 4, 0, 0] },
            data: trend.map(item => round2(item.costYuan))
          },
          {
            name: '毛利',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: { width: 2.5, color: '#34d399' },
            itemStyle: { color: '#34d399', borderColor: '#062f22', borderWidth: 2 },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(52, 211, 153, 0.28)' },
                  { offset: 1, color: 'rgba(52, 211, 153, 0)' }
                ]
              }
            },
            data: trend.map(item => round2(item.profitYuan))
          }
        ]
      }, true)
    },
    renderProfitGauge() {
      const chart = this.getChart('profitGauge')
      if (!chart) return
      const raw = this.stats.todayProfitRate
      const hasRate = raw !== null && raw !== undefined && raw !== '' && !Number.isNaN(Number(raw))
      const rate = hasRate ? Number(raw) : 0
      chart.setOption({
        series: [
          {
            type: 'gauge',
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: 100,
            radius: '96%',
            center: ['50%', '60%'],
            progress: {
              show: true,
              width: 14,
              roundCap: true,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 1,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#22d3ee' },
                    { offset: 1, color: '#34d399' }
                  ]
                }
              }
            },
            axisLine: { roundCap: true, lineStyle: { width: 14, color: [[1, 'rgba(142, 163, 192, 0.16)']] } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            pointer: { show: false },
            anchor: { show: false },
            title: { show: false },
            detail: {
              valueAnimation: true,
              offsetCenter: [0, '0%'],
              fontSize: 32,
              fontWeight: 700,
              color: '#e6f6ff',
              formatter: () => (hasRate ? `${rate.toFixed(1)}%` : '—')
            },
            data: [{ value: Math.max(Math.min(rate, 100), 0) }]
          }
        ]
      }, true)
    },
    renderGrowthTrend() {
      const chart = this.getChart('growthTrend')
      if (!chart) return
      const trend = this.trend
      chart.setOption({
        tooltip: { ...TOOLTIP, trigger: 'axis' },
        legend: {
          data: ['新增用户', '新增需求'],
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: LEGEND_TEXT
        },
        grid: { left: 8, right: 12, top: 20, bottom: 36, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trend.map(item => item.label),
          axisLine: AXIS_LINE,
          axisTick: { show: false },
          axisLabel: AXIS_LABEL
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: SPLIT_LINE,
          axisLabel: AXIS_LABEL
        },
        series: [
          {
            name: '新增用户',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2.5, color: '#38bdf8' },
            itemStyle: { color: '#38bdf8' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(56, 189, 248, 0.35)' },
                  { offset: 1, color: 'rgba(56, 189, 248, 0)' }
                ]
              }
            },
            data: trend.map(item => item.newUsers)
          },
          {
            name: '新增需求',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2.5, color: '#f59e0b' },
            itemStyle: { color: '#f59e0b' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(245, 158, 11, 0.32)' },
                  { offset: 1, color: 'rgba(245, 158, 11, 0)' }
                ]
              }
            },
            data: trend.map(item => item.newDemands)
          }
        ]
      }, true)
    },
    renderCreditTrend() {
      const chart = this.getChart('creditTrend')
      if (!chart) return
      const trend = this.trend
      chart.setOption({
        tooltip: { ...TOOLTIP, trigger: 'axis' },
        legend: {
          data: ['积分消耗', '活跃用户'],
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: LEGEND_TEXT
        },
        grid: { left: 8, right: 8, top: 20, bottom: 36, containLabel: true },
        xAxis: {
          type: 'category',
          data: trend.map(item => item.label),
          axisLine: AXIS_LINE,
          axisTick: { show: false },
          axisLabel: AXIS_LABEL
        },
        yAxis: [
          {
            type: 'value',
            minInterval: 1,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: SPLIT_LINE,
            axisLabel: AXIS_LABEL
          },
          {
            type: 'value',
            minInterval: 1,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: AXIS_LABEL
          }
        ],
        series: [
          {
            name: '积分消耗',
            type: 'bar',
            barMaxWidth: 20,
            itemStyle: { color: gradient('#a855f7', 'rgba(168, 85, 247, 0.12)'), borderRadius: [4, 4, 0, 0] },
            data: trend.map(item => item.consumeCredits)
          },
          {
            name: '活跃用户',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2.5, color: '#f472b6' },
            itemStyle: { color: '#f472b6' },
            data: trend.map(item => item.activeUsers)
          }
        ]
      }, true)
    },
    renderAccountPie() {
      const chart = this.getChart('accountPie')
      if (!chart) return
      const rows = [
        { name: '已核算', value: Number(this.stats.accountedCount || 0), color: '#34d399' },
        { name: '缺刊例', value: Number(this.stats.unaccountedCount || 0), color: '#f59e0b' },
        { name: '非火山', value: Number(this.stats.nonVolcCount || 0), color: '#38bdf8' }
      ]
      const total = rows.reduce((sum, row) => sum + row.value, 0)
      chart.setOption({
        tooltip: { ...TOOLTIP, trigger: 'item' },
        legend: {
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: LEGEND_TEXT
        },
        series: [
          {
            type: 'pie',
            radius: ['52%', '74%'],
            center: ['50%', '46%'],
            avoidLabelOverlap: true,
            padAngle: 2,
            itemStyle: { borderRadius: 6, borderColor: 'rgba(8, 16, 34, 0.9)', borderWidth: 2 },
            label: {
              show: true,
              position: 'center',
              formatter: () => `{a|${total}}\n{b|消费笔数}`,
              rich: {
                a: { fontSize: 26, fontWeight: 700, color: '#e6f6ff', lineHeight: 34 },
                b: { fontSize: 11, color: '#8ea3c0' }
              }
            },
            emphasis: { label: { show: true }, scale: true, scaleSize: 6 },
            labelLine: { show: false },
            data: total > 0
              ? rows.map(row => ({ name: row.name, value: row.value, itemStyle: { color: row.color } }))
              : [{ name: '暂无数据', value: 1, itemStyle: { color: 'rgba(142, 163, 192, 0.18)' } }]
          }
        ]
      }, true)
    },
    renderPendingBar() {
      const chart = this.getChart('pendingBar')
      if (!chart) return
      const rows = PENDING_DEFS.map(item => ({
        title: item.title,
        value: Number(this.stats[item.key] || 0),
        color: item.color
      }))
      chart.setOption({
        tooltip: { ...TOOLTIP, trigger: 'item' },
        grid: { left: 8, right: 40, top: 12, bottom: 8, containLabel: true },
        xAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: SPLIT_LINE,
          axisLabel: AXIS_LABEL
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: rows.map(row => row.title),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { ...AXIS_LABEL, fontSize: 12, color: '#c4d4ea' }
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 16,
            showBackground: true,
            backgroundStyle: { color: 'rgba(142, 163, 192, 0.08)', borderRadius: 8 },
            itemStyle: {
              borderRadius: 8,
              color: params => {
                const row = rows[params.dataIndex]
                return {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 1,
                  y2: 0,
                  colorStops: [
                    { offset: 0, color: 'rgba(56, 189, 248, 0.25)' },
                    { offset: 1, color: row ? row.color : '#38bdf8' }
                  ]
                }
              }
            },
            label: { show: true, position: 'right', color: '#e6f6ff', fontWeight: 600 },
            data: rows.map(row => row.value)
          }
        ]
      }, true)
      if (!this.pendingBound) {
        chart.on('click', params => {
          const def = PENDING_DEFS[params.dataIndex]
          if (def) this.goCard(def)
        })
        this.pendingBound = true
      }
    },
    renderTestRing() {
      const chart = this.getChart('testRing')
      if (!chart) return
      const testCost = Number(this.stats.todayTestCostYuan || 0)
      const realCost = Math.max(Number(this.stats.todayVolcCostYuan || 0), 0)
      const total = testCost + realCost
      const ratio = total > 0 ? (testCost / total) * 100 : 0
      chart.setOption({
        tooltip: { ...TOOLTIP, trigger: 'item', valueFormatter: value => `¥${Number(value || 0).toFixed(2)}` },
        series: [
          {
            type: 'pie',
            radius: ['58%', '80%'],
            center: ['50%', '50%'],
            padAngle: 2,
            itemStyle: { borderRadius: 6, borderColor: 'rgba(8, 16, 34, 0.9)', borderWidth: 2 },
            label: {
              show: true,
              position: 'center',
              formatter: () => `{a|${ratio.toFixed(1)}%}\n{b|测试成本占比}`,
              rich: {
                a: { fontSize: 24, fontWeight: 700, color: '#fb923c', lineHeight: 32 },
                b: { fontSize: 11, color: '#8ea3c0' }
              }
            },
            labelLine: { show: false },
            data: total > 0
              ? [
                { name: '测试成本', value: testCost, itemStyle: { color: '#fb923c' } },
                { name: '真实成本', value: realCost, itemStyle: { color: 'rgba(56, 189, 248, 0.65)' } }
              ]
              : [{ name: '暂无成本', value: 1, itemStyle: { color: 'rgba(142, 163, 192, 0.18)' } }]
          }
        ]
      }, true)
    },
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        Object.keys(this.charts).forEach(key => {
          if (this.charts[key]) this.charts[key].resize()
        })
      }, 150)
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) this.charts[key].dispose()
      })
      this.charts = {}
    },
    goCard(item) {
      if (!item || !item.path) return
      this.$router.push({ path: item.path, query: item.query || {} })
    }
  }
}

function round2(value) {
  return Math.round(Number(value || 0) * 100) / 100
}
</script>

<style scoped>
.tech-dash {
  position: relative;
  margin: -20px;
  padding: 22px 24px 28px;
  min-height: calc(100vh - 60px);
  box-sizing: border-box;
  background:
    radial-gradient(1200px 600px at 12% -10%, rgba(56, 189, 248, 0.16), transparent 60%),
    radial-gradient(900px 500px at 100% 0%, rgba(168, 85, 247, 0.14), transparent 55%),
    linear-gradient(165deg, #060c1a 0%, #0b1630 48%, #080f22 100%);
  color: #e6f6ff;
  overflow: hidden;
}
.dash-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(142, 163, 192, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(142, 163, 192, 0.05) 1px, transparent 1px);
  background-size: 44px 44px, 44px 44px;
}
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
}
.orb-1 {
  width: 260px;
  height: 260px;
  top: -80px;
  left: 18%;
  background: rgba(34, 211, 238, 0.35);
}
.orb-2 {
  width: 220px;
  height: 220px;
  top: 40%;
  right: -60px;
  background: rgba(168, 85, 247, 0.28);
}
.orb-3 {
  width: 200px;
  height: 200px;
  bottom: -70px;
  left: 40%;
  background: rgba(16, 185, 129, 0.22);
}

/* header */
.dash-head {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}
.dash-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 1px;
  background: linear-gradient(92deg, #7dd3fc, #e0f2fe 45%, #c4b5fd);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 24px rgba(56, 189, 248, 0.25);
}
.dash-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #7f95b3;
  letter-spacing: 2px;
}
.head-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.live-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  color: #a7f3d0;
  border-radius: 999px;
  border: 1px solid rgba(52, 211, 153, 0.3);
  background: rgba(16, 185, 129, 0.1);
}
.live-chip b {
  color: #ecfdf5;
}
.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6);
  animation: pulse 1.8s infinite;
}
@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6); }
  70% { box-shadow: 0 0 0 8px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
}
.clock {
  font-size: 13px;
  color: #9fb6d4;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
}
.refresh-btn {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.35);
  color: #bae6fd;
}
.refresh-btn:hover,
.refresh-btn:focus {
  background: rgba(56, 189, 248, 0.22);
  border-color: rgba(56, 189, 248, 0.6);
  color: #e0f2fe;
}

/* kpi */
.kpi-card {
  position: relative;
  margin-bottom: 16px;
  padding: 18px 18px 14px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(142, 163, 192, 0.16);
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02));
  backdrop-filter: blur(10px);
  transition: transform 0.25s, border-color 0.25s, box-shadow 0.25s;
}
.kpi-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, var(--accent), transparent);
}
.kpi-card:hover {
  transform: translateY(-3px);
  border-color: rgba(56, 189, 248, 0.4);
  box-shadow: 0 12px 30px rgba(2, 8, 23, 0.55);
}
.kpi-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.kpi-label {
  font-size: 13px;
  color: #93a9c7;
  letter-spacing: 0.5px;
}
.kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: var(--accent);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.kpi-value {
  margin-top: 10px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.15;
  color: #f0f9ff;
  text-shadow: 0 0 18px rgba(56, 189, 248, 0.28);
  font-variant-numeric: tabular-nums;
}
.kpi-foot {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.growth {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 7px;
  border-radius: 999px;
  font-weight: 600;
}
.growth.up {
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.14);
}
.growth.down {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.14);
}
.growth.flat {
  color: #a3b6d1;
  background: rgba(142, 163, 192, 0.12);
}
.kpi-tip {
  color: #74899f;
}
.kpi-spark {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 34px;
  opacity: 0.85;
}

/* panels */
.panel-row {
  margin-top: 2px;
}
.panel {
  position: relative;
  margin-bottom: 16px;
  padding: 16px 18px 14px;
  border-radius: 14px;
  border: 1px solid rgba(142, 163, 192, 0.16);
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.015));
  backdrop-filter: blur(10px);
}
.panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #dbeafe;
  letter-spacing: 0.5px;
}
.panel-head em {
  margin-left: auto;
  font-style: normal;
  font-size: 11px;
  font-weight: 400;
  color: #74899f;
}
.head-bar {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: linear-gradient(180deg, #22d3ee, #a855f7);
}
.chart {
  width: 100%;
}
.chart-lg {
  height: 320px;
}
.chart-md {
  height: 250px;
}
.chart-gauge {
  height: 170px;
}
.chart-ring {
  height: 168px;
}

/* mini stats */
.mini-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}
.mini-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  background: rgba(142, 163, 192, 0.08);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.mini-item:hover {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.32);
}
.mini-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.mini-label {
  font-size: 12px;
  color: #9fb6d4;
}
.mini-value {
  margin-left: auto;
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* quick entries */
.quick-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 2px;
}
.quick-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 180px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(142, 163, 192, 0.16);
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.015));
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}
.quick-chip:hover {
  transform: translateY(-2px);
  border-color: rgba(56, 189, 248, 0.4);
}
.quick-chip i {
  font-size: 16px;
  color: #7dd3fc;
}
.quick-label {
  font-size: 12px;
  color: #9fb6d4;
}
.quick-value {
  margin-left: auto;
  font-size: 16px;
  font-weight: 700;
  color: #f0f9ff;
  font-variant-numeric: tabular-nums;
}
</style>
